import {useEffect, useMemo, useRef, useState} from "react";

import useApi from "./useApi.js";
import useMountedState from "./useMountedState.js";
import {
  buildNotificationPreviewPayload,
  getEmptyPreviewKind
} from "../pages/facility/notificationPreviewUtils.js";

/**
 * Debounced notification-preview fetch for Configure panel and large preview modal.
 */
const useNotificationEmailPreview = ({
  facilityId,
  type,
  frequency = null,
  state = null,
  includeTags = false,
  filterChecksheets = false,
  checksheetIds = null,
  enabled = true
}) => {
  const isMounted = useMountedState();
  const {api: apiPreview} = useApi("notification-preview", {
    suppress: {success: true, error: true}
  });

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewRequestId = useRef(0);

  const previewReady = !!(enabled && type);
  const checksheetIdsKey = useMemo(
    () => (Array.isArray(checksheetIds) ? checksheetIds.join(",") : ""),
    [checksheetIds]
  );

  useEffect(() => {
    if (!isMounted() || !facilityId || !type || !enabled) {
      if (!type) {
        setPreview(null);
        setPreviewLoading(false);
        setPreviewFailed(false);
      }
      return undefined;
    }

    const requestId = ++previewRequestId.current;
    setPreviewLoading(true);
    setPreviewFailed(false);

    const timer = setTimeout(() => {
      const ids = checksheetIdsKey
        ? checksheetIdsKey.split(",").map(id => (Number.isNaN(Number(id)) ? id : Number(id)))
        : null;

      const payload = buildNotificationPreviewPayload({
        facilityId,
        type,
        frequency,
        state,
        includeTags,
        filterChecksheets,
        checksheetIds: ids
      });

      apiPreview.callPost(payload).then(response => {
        if (!isMounted() || requestId !== previewRequestId.current) return;
        if (response?.status === 200) {
          setPreview(response.data);
          setPreviewFailed(false);
        } else {
          setPreview(null);
          setPreviewFailed(true);
        }
        setPreviewLoading(false);
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [
    isMounted,
    facilityId,
    type,
    frequency,
    state,
    includeTags,
    filterChecksheets,
    checksheetIdsKey,
    enabled,
    apiPreview
  ]);

  const previewSubject =
    preview?.subject || (previewReady ? "Loading preview…" : "Select a notification type");

  return {
    preview,
    previewLoading,
    previewFailed,
    previewReady,
    previewSubject,
    emptyPreviewKind: getEmptyPreviewKind(type)
  };
};

export default useNotificationEmailPreview;
